import LegalDocument, {
  type LegalDocumentSection,
} from "@/components/LegalDocument";
import {
  SEO_DEFAULT_ORIGIN,
  SEO_DEFAULT_SOCIAL_IMAGE,
  type SeoMetadata,
} from "@shared/seo";

const SUPPORT_EMAIL = "support@techfare.com";
const EFFECTIVE_DATE = "March 21, 2026";

const TOS_SECTIONS: LegalDocumentSection[] = [
  {
    id: "using-techfare",
    title: "Using techfare",
    summary:
      "Without the express written consent of techfare LLC, you may not:",
    paragraphs: [],
    bullets: [
      "Modify or copy the materials",
      "Use the materials for any commercial purpose or for public display (commercial or non-commercial)",
      "Attempt to decompile or reverse engineer any software on the website",
      "Remove any copyright or proprietary notations from the materials",
      'Transfer the materials to another person or "mirror" the materials on any other server',
    ],
  },
  {
    id: "paypal-auth",
    title: "PayPal Authentication for Payouts",
    summary:
      'For sellers using techfare, we require authentication through "Log in with PayPal" to securely link your PayPal account for payouts.',
    paragraphs: [
      'For sellers using techfare, we require authentication through "Log in with PayPal" to securely link your PayPal account for payouts. This ensures that payouts are processed only to verified PayPal accounts, enhancing transaction security and reducing errors. This feature is strictly for associating your PayPal account with your techfare profile for payment purposes and does not serve as a login method for accessing the techfare platform. By authenticating with PayPal, you consent to the use of your PayPal account for processing payments related to your sales on techfare.',
    ],
  },
  {
    id: "stripe-payouts",
    title: "Stripe Payouts and Card Payments",
    summary:
      "Sellers can also connect their own Stripe account so buyers can pay them by card, Apple Pay or Google Pay.",
    paragraphs: [
      "Sellers can connect their own Stripe account to techfare as an alternative or addition to PayPal. Stripe verifies the seller's identity and bank details during setup, and use of a Stripe account is subject to the Stripe Services Agreement. When a buyer pays a seller through Stripe, the payment goes directly to the seller's Stripe account, the seller is the merchant of record for that sale and pays Stripe's processing fees, and techfare's platform fee is deducted from the payment at checkout.",
      "Payment disputes are decided by the payment provider, not by techfare. Buyers who paid with PayPal can open a dispute through PayPal, and buyers who paid by card through Stripe can dispute the charge with their card issuer. Sellers respond to those disputes through PayPal or their Stripe Dashboard.",
    ],
  },
  {
    id: "data-redistribution",
    title: "Data Redistribution",
    summary:
      "A user sign up with techfare does not grant you the rights to redistribute or resell techfare data.",
    paragraphs: [
      "A user sign up with techfare does not grant you the rights to redistribute or resell techfare data. If you intend to redistribute or resell any data from the techfare website, you must obtain the appropriate data approval from techfare. Please contact us at to request a price quote for redistributing techfare data.",
    ],
  },
  {
    id: "listings",
    title: "Listings",
    summary:
      "The listings displayed on techfare are created by individuals over whom techfare LLC has no control.",
    paragraphs: [
      "The listings displayed on techfare are created by individuals over whom techfare LLC has no control. You understand that techfare LLC has no control over the listings or other information posted on the website. The listings displayed on techfare are automated and are not screened by techfare. techfare LLC cannot guarantee the accuracy, completeness, legality, reliability, or availability of any listing, item, information, or website displayed on techfare. Each listing, including its information and images, is copyrighted by its respective owner(s).",
      "When you click on links to various merchants on this site and make a purchase, this can result in this site earning a commission. Affiliate programs and affiliations include, but are not limited to, the eBay Partner Network.",
    ],
  },
  {
    id: "selling",
    title: "Selling on techfare",
    summary:
      "When you choose to sell items to techfare, please be aware of the following terms:",
    paragraphs: [
      "When you choose to sell items to techfare, please be aware of the following terms:",
    ],
    bullets: [
      "Binding Sales to techfare: All sales made by sellers to techfare are considered binding agreements. Once a seller accepts an offer from techfare to purchase their item, it is expected that the seller will fulfill the transaction promptly and in accordance with the agreed terms.",
      "techfare's Right to Cancel Transactions: techfare reserves the right to cancel any transaction initiated by the seller without the need for prior conversation with the seller. This cancellation may occur for various reasons, including but not limited to: Violation of techfare's Terms of Service or any other policies. Suspicion of fraudulent activity. Non-compliance with applicable laws or regulations. Technical issues or errors in the listing. Concerns about the safety and security of users on the platform.",
      "techfare's Purchase of Phones: techfare may, at its discretion, consider purchasing phones from sellers at prices that are typically slightly lower than the prevailing market rates. Please note that the availability of this purchase option may vary and depends on techfare's current inventory and operational requirements.",
      "Stolen Devices: techfare takes the issue of stolen devices seriously. Before completing any transaction, techfare conducts thorough checks, including IMEI checks and other methods, to verify the legitimacy of the device being sold. If techfare determines that a device is reported as stolen or involved in any illegal activity, the transaction will be canceled, and appropriate legal authorities may be notified.",
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use Policy",
    summary:
      "Techfare is committed to providing a safe, secure, and trustworthy platform for users to buy and sell technology devices.",
    paragraphs: [
      "Techfare is committed to providing a safe, secure, and trustworthy platform for users to buy and sell technology devices. To maintain the integrity of our platform, we prohibit the use of our services for certain activities, industries, and goods outlined below:",
    ],
  },
  {
    id: "prohibited-activities",
    title: "Prohibited Activities",
    summary:
      "Activities that are prohibited on the techfare platform.",
    paragraphs: [],
    bullets: [
      "Illegal activities, such as violations of applicable laws and regulations.",
      "Sale of stolen, counterfeit, or fraudulent items.",
      "Engaging in scams, fraudulent schemes, or unauthorized payment activities.",
      "Using bots or scraping tools to extract data from the platform.",
      "Interfering with the security or functionality of the Techfare platform.",
    ],
  },
  {
    id: "prohibited-industries",
    title: "Prohibited Industries",
    summary:
      "Industries that are not permitted to use the techfare platform.",
    paragraphs: [],
    bullets: [
      "Weapons and ammunition.",
      "Controlled substances and prescription drugs.",
      "Adult content or sexually explicit materials.",
      "Financial products such as cryptocurrencies or credit cards.",
      "Gambling and betting services.",
      "Harmful or hazardous items.",
    ],
  },
  {
    id: "prohibited-goods",
    title: "Prohibited Goods",
    summary:
      "Goods that are not allowed to be listed or sold on the techfare platform.",
    paragraphs: [],
    bullets: [
      "Non-technology items unrelated to consumer electronics.",
      "Counterfeit or unauthorized replicas.",
      "Devices marked as stolen or flagged in IMEI databases.",
    ],
  },
  {
    id: "consequences",
    title: "Consequences",
    summary:
      "Violating the Acceptable Use Policy may result in account suspension, removal of prohibited listings, or reporting to appropriate authorities.",
    paragraphs: [
      "Violating this Acceptable Use Policy may result in account suspension, removal of prohibited listings, or reporting to appropriate authorities.",
    ],
  },
  {
    id: "disclaimer",
    title: "Disclaimer",
    summary:
      'The materials on techfare\'s website are provided "as is." techfare LLC makes no warranties, expressed or implied.',
    paragraphs: [
      "The materials on techfare's website are provided \"as is.\" techfare LLC makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties, including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.",
      "Furthermore, techfare LLC does not warrant or make any representations concerning the accuracy, likely results, or reliability of the use of the materials on its website or otherwise relating to such materials or on any sites linked to techfare.",
    ],
  },
  {
    id: "limitations",
    title: "Limitations of Liability",
    summary:
      "In no event shall techfare LLC or its suppliers be liable for any damages arising out of the use or inability to use the materials on techfare's website.",
    paragraphs: [
      "In no event shall techfare LLC or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on techfare's website, even if techfare LLC or an authorized representative has been notified orally or in writing of the possibility of such damage. As some jurisdictions do not allow limitations on implied warranties or limitations of liability for consequential or incidental damages, these limitations may not apply to you.",
    ],
  },
  {
    id: "accuracy",
    title: "Accuracy of Materials",
    summary:
      "The materials appearing on techfare's website could include technical, typographical, or photographic errors.",
    paragraphs: [
      "The materials appearing on techfare's website could include technical, typographical, or photographic errors. techfare LLC does not warrant that any of the materials on its website are accurate, complete, or current. techfare LLC may make changes to the materials contained on its website at any time without notice.",
    ],
  },
  {
    id: "communications",
    title: "Communications",
    summary:
      "By creating an account on techfare, you explicitly grant permission for techfare LLC to send you email communications.",
    paragraphs: [
      "By creating an account on techfare, you explicitly grant permission for techfare LLC to send you email communications with personalized recommendations or service and feature announcements. You have the option to modify and disable these preferences in your user profile settings at any time.",
    ],
  },
  {
    id: "modifications",
    title: "Modifications",
    summary:
      "techfare LLC may revise these terms of service for its website at any time without notice.",
    paragraphs: [
      "techfare LLC may revise these terms of service for its website at any time without notice. By using techfare's website, you are agreeing to be bound by the most current version of these terms of service.",
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law",
    summary:
      "These terms and conditions are governed by and construed in accordance with the laws of the United States.",
    paragraphs: [
      "These terms and conditions are governed by and construed in accordance with the laws of the United States. You irrevocably submit to the exclusive jurisdiction of the courts in that State or location.",
    ],
  },
];

function buildTosMetadata(): SeoMetadata {
  const origin =
    typeof window === "undefined"
      ? SEO_DEFAULT_ORIGIN
      : window.location.origin.replace(/\/+$/, "");

  return {
    title: "Terms of Service | Techfare",
    description:
      "Read Techfare Terms of Service, including usage policies, seller terms, acceptable use, and legal disclaimers.",
    canonicalUrl: `${origin}/terms-of-service`,
    robots:
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    ogType: "website",
    ogImage: SEO_DEFAULT_SOCIAL_IMAGE,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Techfare Terms of Service",
        description:
          "Techfare terms of service covering usage policies, seller terms, acceptable use, and legal disclaimers.",
        url: `${origin}/terms-of-service`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${origin}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Terms of Service",
            item: `${origin}/terms-of-service`,
          },
        ],
      },
    ],
  };
}

export default function TermsOfService() {
  return (
    <LegalDocument
      metadata={buildTosMetadata()}
      badge="Terms of Service"
      title="Terms of Service"
      intro='By accessing and using the website at techfare.com ("techfare"), you agree to comply with these terms of service, as well as all applicable laws and regulations. You acknowledge that you are solely responsible for adhering to any relevant local laws. If you do not agree with any of these terms, you are prohibited from using or accessing this site. The materials on this website are protected by copyright and trademark laws.'
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={EFFECTIVE_DATE}
      sections={TOS_SECTIONS}
      contactEmail={SUPPORT_EMAIL}
      contactLabel={SUPPORT_EMAIL}
      relatedLinks={[
        {
          label: "Privacy Policy",
          href: "/privacy-policy",
        },
      ]}
    />
  );
}
